import { ChevronRight } from 'lucide-react';
import { type ReactNode, useId, useState } from 'react';
import { cn } from '../../lib/utils';

/**
 * Một mục có tiêu đề bấm để mở/gập — người dùng yêu cầu 2026-11-02 cho drawer
 * chi tiết việc: "Baseline, kế hoạch và thực tế" và "Phát triển" là thứ người
 * ta tra khi cần, không phải thứ đọc mỗi lần mở việc.
 *
 * Gập thì nội dung **không render** (không chỉ ẩn bằng CSS): khối bên trong có
 * thể nặng (danh sách MR, commit) và không ai cần nó cho tới khi mở.
 */
export function CollapsibleSection({
  title,
  aside,
  defaultOpen = false,
  className,
  bodyClassName,
  children,
  testId,
}: {
  readonly title: string;
  /** Chữ nhỏ bên phải tiêu đề — vẫn hiện khi đang gập. */
  readonly aside?: ReactNode;
  readonly defaultOpen?: boolean;
  readonly className?: string | undefined;
  readonly bodyClassName?: string | undefined;
  readonly children: ReactNode;
  readonly testId?: string;
}): ReactNode {
  const [open, setOpen] = useState(defaultOpen);
  const bodyId = useId();

  return (
    <section aria-label={title} data-testid={testId} className={cn('min-w-0', className)}>
      <h3 className="flex min-w-0 items-center gap-2">
        <button
          type="button"
          aria-expanded={open}
          aria-controls={bodyId}
          onClick={() => setOpen((value) => !value)}
          className="-mx-1 flex min-w-0 flex-1 items-center gap-1 rounded-control px-1 py-0.5 text-left text-xs font-medium text-muted-foreground hover:text-foreground"
        >
          <ChevronRight
            aria-hidden
            className={cn('size-3.5 shrink-0 transition-transform', open && 'rotate-90')}
          />
          <span className="truncate">{title}</span>
        </button>
        {aside ? (
          <span className="shrink-0 text-[11px] text-muted-foreground">{aside}</span>
        ) : null}
      </h3>
      {open ? (
        <div id={bodyId} className={cn('mt-2', bodyClassName)}>
          {children}
        </div>
      ) : null}
    </section>
  );
}
